// src/pages/ManualMarketing/ManualMarketingGallery/Step2.jsx

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { SingleButton } from "../../../assets/components/ButtonGroup";
import { SelectCard, SelectCardWithSub } from "../../../assets/components/SelectGroup";
import { HeaderIconLActionR } from "../../../assets/components/HeaderGroup";
import { useUIState } from "../../../bridges/uiStateContext";
import arrowleft from "../../../assets/icons/Arrow/left.svg";
import { Chip } from "../../../assets/components/Chip";

import AgeSelectSheet from "../../Ads/components/BottomSheet/AgeSelectSheet";
import ChanneSelectSheet from "../../Ads/components/BottomSheet/ChannelSelectSheet";
import TitleSelectSheet from "../../Ads/components/BottomSheet/TitleSelectSheet";
import AIStyleOptionSheet from "../../Ads/components/BottomSheet/AIStyleOptionSheet";
import useImageListStore from "../../../stores/imageList";
import { useToast } from "../../../hooks/useToast";

// ─────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────

// 로컬스토리지 기준으로만 aiTitle 사용
const THEME_OPTIONS = ["매장 홍보", "상품 소개", "이벤트"];
const norm = (s = "") => String(s).trim().replace(/\s+/g, "").toLowerCase();
const isNonEmpty = (v) => String(v ?? "").trim() !== "";
const firstNonEmpty = (...vals) => {
  for (const v of vals) if (isNonEmpty(v)) return v;
  return "";
};

const mapAiToTheme = (val) => {
  if (val == null) return null;
  const s = String(val).trim();
  const byCode = { "1": "매장 홍보", "2": "상품 소개", "3": "이벤트" };
  if (byCode[s]) return byCode[s];
  const hit = THEME_OPTIONS.find((t) => norm(t) === norm(s));
  return hit || null;
};

const AGE_LABEL_MAP = {
  "1": "10대",
  "2": "20대",
  "3": "30대",
  "4": "40대",
  "5": "50대",
  "6": "60대 이상",
};

const AGE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(AGE_LABEL_MAP).map(([code, label]) => [label, code])
);

const mapAiAgeCodeToLabel = (aiAge) => {
  if (!aiAge) return "";
  const code = String(aiAge);
  return AGE_LABEL_MAP[code] || "";
};

const mapAgeLabelToCode = (label) => {
  const key = String(label ?? "").trim();
  return AGE_LABEL_TO_CODE[key] || null;
};

// 시트 value → 채널
const fromSheetChannelValue = (selected) => {
  if (selected === "카카오톡") return { channel: "카카오톡", subChannel: "" };
  if (selected === "네이버 밴드" || selected === "네이버밴드")
    return { channel: "네이버 밴드", subChannel: "" };

  if (String(selected).startsWith("인스타")) {
    return {
      channel: "인스타그램",
      subChannel: String(selected).includes("스토리") ? "스토리" : "게시물",
    };
  }

  return { channel: selected, subChannel: "" };
};

// aiChannel 코드 → 시트 value
const mapAiChannelCodeToSheetValue = (code) => {
  const c = String(code);
  switch (c) {
    case "1":
      return "카카오톡";
    case "2":
      return "인스타 스토리";
    case "3":
      return "인스타 게시물";
    case "6":
      return "네이버 밴드";
    default:
      return "";
  }
};

// channel/subChannel -> aiChannel(코드)
const mapSelectedChannelToAiCode = (channel, subChannel) => {
  const ch = String(channel ?? "").trim();
  const sub = String(subChannel ?? "").trim();

  if (ch === "카카오톡") return "1";
  if (ch === "인스타그램" && sub === "스토리") return "2";
  if (ch === "인스타그램" && sub === "게시물") return "3";
  if (ch === "블로그") return "4";
  if (ch === "MMS(문자)") return "5";
  if (ch === "네이버 밴드" || ch === "네이버밴드") return "6";
  if (ch === "X(트위터)") return "7";
  return null;
};

const TITLE_CODE_TO_THEME = {
  "1": "매장 홍보",
  "2": "상품 소개",
  "3": "이벤트",
};

const mapTitleCodeToTheme = (v) =>
  TITLE_CODE_TO_THEME[String(v ?? "").trim()] || "";

const mapThemeToTitleCode = (label) => {
  const hit = THEME_OPTIONS.find((t) => norm(t) === norm(label));
  return hit ? String(THEME_OPTIONS.indexOf(hit) + 1) : "";
};

// localStorage 읽기 헬퍼
const readLS = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// 하드 디폴트(최후): 빈 값 방지용
const HARD_DEFAULTS = {
  age: "30대",
  channel: "카카오톡",
  subChannel: "",
  style: "2", // 실사 이미지
  theme: "매장 홍보",
};

const STYLE_LABEL = {
  1: "3D 이미지",
  2: "실사 이미지",
  3: "캐릭터/만화",
  4: "레트로",
  5: "AI모델",
  6: "예술",
};

// (channel, subChannel) → sheet value
const toSheetChannelValue = (channel, subChannel) => {
  if (!channel) return "";
  if (channel === "카카오톡") return "카카오톡";
  if (channel === "네이버밴드" || channel === "네이버 밴드") return "네이버 밴드";
  if (channel === "인스타그램") {
    if (subChannel === "스토리") return "인스타 스토리";
    if (subChannel === "게시물") return "인스타 게시물";
    return "인스타그램";
  }
  return "";
};

// 인스타면 subChannel 필수
const isChannelComplete = (channel, subChannel) => {
  if (!isNonEmpty(channel)) return false;
  if (channel !== "인스타그램") return true;
  return isNonEmpty(subChannel);
};

// data/exData merge(표시용)
const mergeForDisplay = (data, exData) => {
  const d = data ?? {};
  const e = exData ?? {};

  const pick = (a, b) => (isNonEmpty(a) ? a : b);

  return {
    ...e,
    ...d,
    age: pick(d.age, e.age) || "",
    channel: pick(d.channel, e.channel) || "",
    subChannel: pick(d.subChannel, e.subChannel) || "",
    style: pick(d.style, e.style) || "",
    prompt: pick(d.prompt, e.prompt) || "",
    aiThumbnail: d.aiThumbnail ?? e.aiThumbnail ?? null,

    theme: pick(d.theme, e.theme) || "",
    title: pick(d.title, e.title) || "",
    customText: d.customText ?? e.customText ?? d.aiTitleText ?? e.aiTitleText ?? "",
  };
};

// ─────────────────────────────────────────────────────────────
// main
// ─────────────────────────────────────────────────────────────

export default function Step2({
  data,
  updateData,
  onBack,
  onNext,
  onHome,
  aiAge,
  aiChannel,
  aiTitle,
  aiStyle,
  exData,
  headerSelectedPath,
  isRegen,
}) {
  const { registerPageBackHandler } = useUIState();
  const { getImageList } = useImageListStore();
  const { showToast } = useToast();

  const didInitRef = useRef(false);

  // 하드웨어 뒤로가기 → step back
  useEffect(() => {
    if (!registerPageBackHandler) return;
    const unregister = registerPageBackHandler(() => onBack?.());
    return () => unregister();
  }, [onBack, registerPageBackHandler]);

  // 표시용 합성 데이터
  const ex = useMemo(() => mergeForDisplay(data, exData), [data, exData]);

  // localStorage + props 통합 (AI 후보)
  const aiAgeLS = useMemo(() => readLS("aiAge"), []);
  const aiChannelLS = useMemo(() => readLS("aiChannel"), []);
  const aiStyleLS = useMemo(() => readLS("aiStyle"), []);
  const aiTitleLS = useMemo(() => readLS("aiTitle"), []);

  const aiAgeRaw = data?.aiAge ?? aiAge ?? aiAgeLS ?? null;
  const aiChannelRaw = data?.aiChannel ?? aiChannel ?? aiChannelLS ?? null;
  const aiTitleRaw = data?.aiTitle ?? aiTitle ?? aiTitleLS ?? null;
  const aiStyleRaw = data?.aiStyle ?? aiStyle ?? aiStyleLS ?? null;

  const aiTitleLabelSafe = useMemo(() => mapAiToTheme(aiTitleRaw), [aiTitleRaw]);

  const aiTitleCodeSafe = useMemo(() => {
    if (!aiTitleLabelSafe) return "";
    const idx = THEME_OPTIONS.indexOf(aiTitleLabelSafe);
    return idx >= 0 ? String(idx + 1) : "";
  }, [aiTitleLabelSafe]);

  // 이 매장의 이미지 리스트
  const storeBusinessNumber =
    ex?.store_business_number ||
    (typeof localStorage !== "undefined" ? localStorage.store_business_number : null);

  const imageManualList = useMemo(() => {
    if (!storeBusinessNumber) return [];
    return getImageList(storeBusinessNumber) || [];
  }, [storeBusinessNumber, getImageList]);

  // ── local states (UI 표시용) ───────────────────────────
  const [age, setAge] = useState(ex?.age || "");
  const [channel, setChannel] = useState(ex?.channel || "");
  const [subChannel, setSubChannel] = useState(ex?.subChannel || "");
  const [style, setStyle] = useState(ex?.style || "");
  const [theme, setTheme] = useState(
    (ex?.theme || mapTitleCodeToTheme(ex?.title) || "").trim()
  );
  const [customText, setCustomText] = useState(ex?.customText ?? "");
  const [title, setTitle] = useState(ex?.title || mapThemeToTitleCode((ex?.theme || "").trim()) || "");
  const [localPrompt, setLocalPrompt] = useState(ex?.prompt || "");
  const [aiThumbnail, setAiThumbnail] = useState(ex?.aiThumbnail || null);

  const [activeSheet, setActiveSheet] = useState(null);
  const openSheet = (key) => setActiveSheet(key);
  const closeSheet = () => setActiveSheet(null);

  // ─────────────────────────────────────────────────────────────
  // patch → local state 적용 (여기 한 군데로 모으기)
  // ─────────────────────────────────────────────────────────────
  const applyPatchToLocal = useCallback(
    (patch = {}) => {
      if ("age" in patch) setAge(patch.age ?? "");
      if ("channel" in patch) setChannel(patch.channel ?? "");
      if ("subChannel" in patch) setSubChannel(patch.subChannel ?? "");
      if ("style" in patch) setStyle(patch.style ? String(patch.style) : "");
      if ("theme" in patch) setTheme((patch.theme ?? "").trim());
      if ("title" in patch) setTitle(patch.title ? String(patch.title) : "");
      if ("customText" in patch) setCustomText(patch.customText ?? "");
      if ("prompt" in patch) setLocalPrompt(patch.prompt ?? "");
      if ("aiThumbnail" in patch) setAiThumbnail(patch.aiThumbnail ?? null);
    },
    []
  );

  // ─────────────────────────────────────────────────────────────
  // style 기반 prompt/thumb 자동 보완(있을 때만)
  // ─────────────────────────────────────────────────────────────
  const fillPromptThumbByStyle = useCallback(
    ({ pickedStyle, currentPrompt, currentThumb }) => {
      const s = String(pickedStyle ?? "").trim();
      if (!s) return { prompt: currentPrompt, aiThumbnail: currentThumb };

      const candidates = imageManualList.filter((img) => String(img.design_id) === s);
      if (candidates.length <= 0) return { prompt: currentPrompt, aiThumbnail: currentThumb };

      const randomImg = candidates[Math.floor(Math.random() * candidates.length)];
      return {
        prompt: isNonEmpty(currentPrompt) ? currentPrompt : (randomImg.prompt || ""),
        aiThumbnail: currentThumb != null ? currentThumb : (randomImg.path || null),
      };
    },
    [imageManualList]
  );

  // ─────────────────────────────────────────────────────────────
  // ✅ 초기값 결정(딱 1번)
  //   - 표시(local state): "유저값 > exData > AI추천 > 하드디폴트"
  //   - updateData(): data의 빈칸만 patch로 채움(덮어쓰기 금지)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (didInitRef.current) return;

    const d = data ?? {};   // 유저 선택값(최우선)
    const e = exData ?? {}; // 외부 유입값(재생성/캘린더/로딩 등)

    // 1) AGE
    const resolvedAge = firstNonEmpty(
      d.age,
      e.age,
      mapAiAgeCodeToLabel(aiAgeRaw),
      HARD_DEFAULTS.age
    );
    const resolvedAiAge = firstNonEmpty(
      d.aiAge,
      e.aiAge,
      aiAgeRaw ? String(aiAgeRaw) : "",
      mapAgeLabelToCode(resolvedAge) || ""
    );

    // 2) CHANNEL/SUB
    const dChannelOk = isChannelComplete(d.channel, d.subChannel);
    const eChannelOk = isChannelComplete(e.channel, e.subChannel);

    let resolvedChannel = "";
    let resolvedSubChannel = "";
    let resolvedAiChannel = "";

    if (dChannelOk) {
      resolvedChannel = d.channel;
      resolvedSubChannel = d.subChannel || "";
      resolvedAiChannel = firstNonEmpty(
        d.aiChannel,
        mapSelectedChannelToAiCode(resolvedChannel, resolvedSubChannel) || ""
      );
    } else if (eChannelOk) {
      resolvedChannel = e.channel;
      resolvedSubChannel = e.subChannel || "";
      resolvedAiChannel = firstNonEmpty(
        e.aiChannel,
        mapSelectedChannelToAiCode(resolvedChannel, resolvedSubChannel) || ""
      );
    } else if (aiChannelRaw) {
      const selectedForSheet = mapAiChannelCodeToSheetValue(aiChannelRaw);
      const { channel: ch, subChannel: sub } = selectedForSheet
        ? fromSheetChannelValue(selectedForSheet)
        : { channel: "", subChannel: "" };

      resolvedChannel = ch || HARD_DEFAULTS.channel;
      resolvedSubChannel = ch === "인스타그램" ? sub : "";
      resolvedAiChannel = String(aiChannelRaw);
    } else {
      resolvedChannel = HARD_DEFAULTS.channel;
      resolvedSubChannel = HARD_DEFAULTS.subChannel;
      resolvedAiChannel = mapSelectedChannelToAiCode(resolvedChannel, resolvedSubChannel) || "";
    }

    // 3) STYLE
    const resolvedStyle = firstNonEmpty(
      d.style,
      e.style,
      aiStyleRaw != null ? String(aiStyleRaw) : "",
      HARD_DEFAULTS.style
    );
    const resolvedAiStyle = firstNonEmpty(
      d.aiStyle,
      e.aiStyle,
      aiStyleRaw != null ? String(aiStyleRaw) : "",
      String(resolvedStyle)
    );

    // 4) THEME/TITLE/CUSTOM
    const themeFromDTitle = mapTitleCodeToTheme(d.title);
    const themeFromETitle = mapTitleCodeToTheme(e.title);

    const resolvedTheme = firstNonEmpty(
      d.theme,
      themeFromDTitle,
      e.theme,
      themeFromETitle,
      aiTitleLabelSafe,
      HARD_DEFAULTS.theme
    );

    const resolvedTitle = firstNonEmpty(
      d.title,
      e.title,
      mapThemeToTitleCode(resolvedTheme),
      mapThemeToTitleCode(HARD_DEFAULTS.theme)
    );

    const resolvedCustomText =
      d.customText ??
      e.customText ??
      d.aiTitleText ??
      e.aiTitleText ??
      "";

    // 5) PROMPT/THUMB
    const basePrompt = firstNonEmpty(d.prompt, e.prompt, localPrompt);
    const baseThumb = d.aiThumbnail ?? e.aiThumbnail ?? aiThumbnail ?? null;

    const filled = fillPromptThumbByStyle({
      pickedStyle: resolvedStyle,
      currentPrompt: basePrompt,
      currentThumb: baseThumb,
    });

    const resolvedPrompt = filled.prompt || "";
    const resolvedThumb = filled.aiThumbnail ?? null;

    // local state 세팅(표시)
    applyPatchToLocal({
      age: resolvedAge,
      channel: resolvedChannel,
      subChannel: resolvedSubChannel,
      style: String(resolvedStyle),
      theme: resolvedTheme,
      title: String(resolvedTitle),
      customText: resolvedCustomText,
      prompt: resolvedPrompt,
      aiThumbnail: resolvedThumb,
    });

    // ✅ data에 동기화: "비어있을 때만" 채움(덮어쓰기 금지)
    const patch = {};

    if (!isNonEmpty(d.age)) patch.age = resolvedAge;
    if (!isNonEmpty(d.aiAge)) patch.aiAge = resolvedAiAge;

    const dChannelMissing = !isChannelComplete(d.channel, d.subChannel);
    if (dChannelMissing) {
      patch.channel = resolvedChannel;
      patch.subChannel = resolvedSubChannel;
      patch.aiChannel = resolvedAiChannel || mapSelectedChannelToAiCode(resolvedChannel, resolvedSubChannel);
    }

    if (!isNonEmpty(d.style)) {
      patch.style = String(resolvedStyle);
      patch.aiStyle = String(resolvedAiStyle);
    }

    // theme/title은 "둘 다 의미있게 없는 경우"만 채우고, 일부만 없으면 그 부분만 채움
    const dHasTheme = isNonEmpty(d.theme);
    const dHasTitle = isNonEmpty(d.title);

    if (!dHasTheme) patch.theme = resolvedTheme;
    if (!dHasTitle) patch.title = String(resolvedTitle);

    // aiTitle은 titleCode 성격이니까, title이 비었을 때만 같이 채우는게 안전
    if (!isNonEmpty(d.aiTitle) && !dHasTitle) patch.aiTitle = String(resolvedTitle);

    // customText: null/undefined만 보정(빈 문자열은 “유저가 비워둔 값”일 수 있음)
    if (d.customText == null) patch.customText = resolvedCustomText;

    if (!isNonEmpty(d.prompt) && isNonEmpty(resolvedPrompt)) patch.prompt = resolvedPrompt;
    if (d.aiThumbnail == null && resolvedThumb != null) patch.aiThumbnail = resolvedThumb;

    if (Object.keys(patch).length > 0) updateData(patch);

    didInitRef.current = true;
  }, [
    data,
    exData,
    aiAgeRaw,
    aiChannelRaw,
    aiStyleRaw,
    aiTitleLabelSafe,
    aiThumbnail,
    localPrompt,
    updateData,
    applyPatchToLocal,
    fillPromptThumbByStyle,
  ]);

  // ✅ 초기화 가능 여부
  const canReset =
    isNonEmpty(age) ||
    isNonEmpty(channel) ||
    isNonEmpty(subChannel) ||
    isNonEmpty(style) ||
    isNonEmpty(theme) ||
    (customText ?? "") !== "" ||
    isNonEmpty(localPrompt) ||
    aiThumbnail != null;

  // 초기화: "빈값" 상태로 만들어줌
  const handleReset = () => {
    applyPatchToLocal({
      age: "",
      channel: "",
      subChannel: "",
      style: "",
      theme: "",
      title: "",
      customText: "",
      prompt: "",
      aiThumbnail: null,
    });

    updateData({
      age: "",
      channel: "",
      subChannel: "",
      style: "",
      theme: "",
      title: "",
      customText: "",
      aiAge: null,
      aiChannel: null,
      aiStyle: null,
      aiTitle: null,
      prompt: null,
      aiThumbnail: null,
    });

    showToast("초기화되었습니다", { placement: "bottom", bottomOffset: "88px" });
  };

  // ─────────────────────────────────────────────────────────────
  // ✅ AI 자동 선택: "현재 비어있는 항목만" 채우기
  // ─────────────────────────────────────────────────────────────
  const makeAiFillPatch = useCallback(() => {
    const patch = {};

    // 1) age
    if (!isNonEmpty(age) && aiAgeRaw) {
      const label = mapAiAgeCodeToLabel(aiAgeRaw);
      if (label) {
        patch.age = label;
        patch.aiAge = String(aiAgeRaw);
      }
    }

    // 2) channel/subChannel (인스타는 sub 필요)
    if (!isChannelComplete(channel, subChannel) && aiChannelRaw) {
      const sheetValue = mapAiChannelCodeToSheetValue(aiChannelRaw);
      if (sheetValue) {
        const { channel: ch, subChannel: sub } = fromSheetChannelValue(sheetValue);
        patch.channel = ch;
        patch.subChannel = ch === "인스타그램" ? sub : "";
        patch.aiChannel = String(aiChannelRaw);
      }
    }

    // 3) theme/title
    if (!isNonEmpty(theme) && aiTitleLabelSafe) {
      const titleCode = mapThemeToTitleCode(aiTitleLabelSafe);
      patch.theme = aiTitleLabelSafe;
      patch.title = titleCode;
      patch.aiTitle = titleCode;
      // customText는 덮어쓰지 않음(이미 있으면 유지)
      if (customText == null) patch.customText = "";
    }

    // 4) style (+ prompt/thumb 보완은 style이 비어있을 때만)
    if (!isNonEmpty(style) && aiStyleRaw != null) {
      const s = String(aiStyleRaw);
      patch.style = s;
      patch.aiStyle = s;

      const hasPrompt = isNonEmpty(localPrompt) || isNonEmpty(data?.prompt);
      const hasThumb = aiThumbnail != null || data?.aiThumbnail != null;

      if (!hasPrompt || !hasThumb) {
        const filled = fillPromptThumbByStyle({
          pickedStyle: s,
          currentPrompt: localPrompt || data?.prompt || "",
          currentThumb: aiThumbnail ?? data?.aiThumbnail ?? null,
        });

        if (!hasPrompt && isNonEmpty(filled.prompt)) patch.prompt = filled.prompt;
        if (!hasThumb && filled.aiThumbnail != null) patch.aiThumbnail = filled.aiThumbnail;
      }
    }

    return patch;
  }, [
    age,
    channel,
    subChannel,
    theme,
    customText,
    style,
    localPrompt,
    aiThumbnail,
    data?.prompt,
    data?.aiThumbnail,
    aiAgeRaw,
    aiChannelRaw,
    aiStyleRaw,
    aiTitleLabelSafe,
    fillPromptThumbByStyle,
  ]);

  const aiFillPatch = useMemo(() => makeAiFillPatch(), [makeAiFillPatch]);
  const isAIApplied = Object.keys(aiFillPatch).length === 0;

  const handleAIAutoSelect = useCallback(() => {
    if (Object.keys(aiFillPatch).length === 0) return;

    // local state 반영
    applyPatchToLocal(aiFillPatch);

    // localStorage: style만 기존처럼 유지
    if (aiFillPatch?.style != null) {
      try {
        localStorage.setItem("aiStyle", String(aiFillPatch.style));
      } catch (e) {
        console.warn("aiStyle localStorage set 실패", e);
      }
    }

    // data 반영
    updateData(aiFillPatch);
  }, [aiFillPatch, applyPatchToLocal, updateData]);

  // ─────────────────────────────────────────────────────────────
  // next
  // ─────────────────────────────────────────────────────────────
  const isChannelValid = isChannelComplete(channel, subChannel);
  const canGoNext = !!(isNonEmpty(age) && isChannelValid && isNonEmpty(style));

  const handleNext = () => {
    if (!canGoNext) return;

    const finalSub = channel === "인스타그램" ? subChannel : "";
    const finalAiAge = mapAgeLabelToCode(age);
    const finalAiChannel = mapSelectedChannelToAiCode(channel, finalSub);

    const patch = {
      age,
      channel,
      subChannel: finalSub,
      style: String(style),
      theme: (theme || "").trim(),
      title: title || mapThemeToTitleCode(theme),
      customText: customText ?? "",
      aiAge: finalAiAge,
      aiChannel: finalAiChannel,
      prompt: (localPrompt || data?.prompt || "").trim() || null,
      aiThumbnail: data?.aiThumbnail ?? aiThumbnail,
      isRegen,
    };

    updateData(patch);
    onNext?.({ ...data, ...patch });
  };

  // ─────────────────────────────────────────────────────────────
  // render
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="relative flex h-dvh min-h-dvh flex-col overflow-hidden bg-surface-default">
      <HeaderIconLActionR
        title=""
        actionText="초기화"
        onAction={canReset ? handleReset : undefined}
        icon={arrowleft}
        onIconClick={onBack}
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-[16px] [touch-action:pan-y] [-webkit-overflow-scrolling:touch]">
        <div className="flex items-center justify-between pb-[32px]">
          <h2 className="text-t2">옵션 설정</h2>
          <Chip
            chip="d"
            label="AI 자동 선택"
            status="selected"
            onClick={() => {
              if (isAIApplied) {
                showToast("이미 선택되어 있습니다", { placement: "bottom", bottomOffset: "88px" });
              } else {
                handleAIAutoSelect();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-[16px]">
          <SelectCard
            label="홍보 고객"
            value={age}
            placeholder="연령대 선택"
            onClick={() => openSheet("age")}
          />

          <SelectCard
            label="홍보 채널"
            value={
              channel === "인스타그램"
                ? subChannel
                  ? `인스타 ${subChannel}`
                  : "인스타그램"
                : channel
            }
            placeholder="채널 선택"
            onClick={() => openSheet("channel")}
          />

          <SelectCardWithSub
            label="맞춤 주제"
            value={(theme || "").trim()}
            subLabel={customText ?? ""}
            subPlaceHolder="작성한 내용이 없습니다"
            placeholder="주제 선택"
            onClick={() => openSheet("topic")}
          />

          <SelectCard
            label="이미지 스타일"
            value={STYLE_LABEL[String(style)] || ""}
            placeholder="스타일 선택"
            onClick={() => openSheet("style")}
          />
        </div>
      </div>

      <AgeSelectSheet
        isOpen={activeSheet === "age"}
        onClose={closeSheet}
        value={age}
        aiAge={aiAgeRaw}
        onConfirm={(nextAge) => {
          const code = mapAgeLabelToCode(nextAge);
          applyPatchToLocal({ age: nextAge });
          updateData({ age: nextAge, aiAge: code });
        }}
      />

      <ChanneSelectSheet
        isOpen={activeSheet === "channel"}
        onClose={closeSheet}
        value={toSheetChannelValue(channel, subChannel)}
        aiChannel={aiChannelRaw}
        onConfirm={(selected) => {
          const { channel: nextChannel, subChannel: nextSub } = fromSheetChannelValue(selected);
          const code = mapSelectedChannelToAiCode(
            nextChannel,
            nextChannel === "인스타그램" ? nextSub : ""
          );

          applyPatchToLocal({ channel: nextChannel, subChannel: nextSub });
          updateData({ channel: nextChannel, subChannel: nextSub, aiChannel: code });
        }}
        height="h-[702px]"
      />

      <TitleSelectSheet
        isOpen={activeSheet === "topic"}
        onClose={closeSheet}
        value={(theme || "").trim()}
        aiTitle={aiTitleLabelSafe}
        aiReco={aiTitleCodeSafe}
        detailValue={customText ?? ""}
        data={data}
        exData={ex}
        updateData={updateData}
        onConfirm={({ theme: nextTheme, customText: nextCustom }) => {
          const titleCode = mapThemeToTitleCode(nextTheme);

          applyPatchToLocal({
            theme: nextTheme,
            title: titleCode,
            customText: nextCustom ?? "",
          });

          updateData({
            theme: nextTheme,
            title: titleCode,
            aiTitle: titleCode,
            customText: nextCustom ?? "",
          });
          closeSheet();
        }}
        height="h-[702px]"
      />

      <AIStyleOptionSheet
        isOpen={activeSheet === "style"}
        onClose={closeSheet}
        exData={{ ...ex, aiThumbnail }}
        value={style}
        aiStyle={aiStyleRaw}
        updateData={(patch) => {
          // sheet 내부에서 style/prompt/thumb을 업데이트할 수 있으니 둘 다 반영
          updateData(patch);
          applyPatchToLocal({
            ...(patch?.style != null ? { style: String(patch.style) } : {}),
            ...(patch?.prompt != null ? { prompt: String(patch.prompt) } : {}),
            ...(patch?.aiThumbnail !== undefined ? { aiThumbnail: patch.aiThumbnail } : {}),
          });
        }}
        onConfirm={({ style: pickedStyle, prompt, aiThumbnail: nextThumb }) => {
          const s = String(pickedStyle);

          const patch = {
            style: s,
            ...(prompt ? { prompt } : {}),
            ...(nextThumb !== undefined ? { aiThumbnail: nextThumb } : {}),
          };

          applyPatchToLocal(patch);
          updateData(patch);
          closeSheet();
        }}
        headerSelectedPath={headerSelectedPath}
      />

      <div
        className="shrink-0 border-t border-border-subtle bg-surface-default/95 backdrop-blur-[8px]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <SingleButton type="fill" disabled={!canGoNext} onClick={handleNext}>
          이미지 생성하기
        </SingleButton>
      </div>
    </div>
  );
}
